/**
 * Show/hide rules per block (K4, Editor 2.0).
 *
 * A block can be wrapped in `{{#np_show <expression>}} ... {{/np_show}}` and
 * is then only shown to contacts who match the expression. The directive is
 * plain text, so it survives the save-time sanitiser, link rewriting and the
 * text part, and it is stored in the template and the frozen snapshot as
 * written. Only the queue consumer (and the view/preview pages) turn it into
 * the Handlebars subset `send/renderTemplate.ts` already renders:
 *
 *   {{#np_show language=de|fr ordered=no}}  ->  {{#if np_s_<hash>}}
 *   {{/np_show}}                            ->  {{/if}}
 *
 * and fill `np_s_<hash>` per recipient with `1` (show) or nothing (hide).
 * A block whose key is missing is hidden, never shown by accident.
 *
 * Expression: space-separated terms, all of which must match (AND). A term is
 * `key=value|value` or `key!=value|value`:
 *
 *   language=de|fr     contact language (two letters)
 *   country=DE|AT      contact country (ISO alpha-2)
 *   segment=<id>|<id>  member of one of these segments (K2 segment library)
 *   label=vip          carries one of these labels
 *   ordered=yes|no     has / has not placed a valid order
 *
 * No quotes, no braces, no semicolons: the text is HTML-escaped and
 * Handlebars-parsed on the way, so the grammar uses only characters that
 * pass through both unchanged.
 *
 * Device rules (desktop only / mobile only) are not per contact: they are CSS
 * classes (`np-d`, `np-m`) with a media query in the mail, see
 * {@link wrapDevice}.
 *
 * This file is pure (no database, no node imports) so the admin app can use it
 * to validate and describe a rule while it is being edited.
 */

export type ShowTermKey = 'language' | 'country' | 'segment' | 'label' | 'ordered';

export interface ShowTerm {
  readonly key: ShowTermKey;
  /** `!=`. For `ordered=no` this is `ordered` with `negate` true. */
  readonly negate: boolean;
  /** Sorted, de-duplicated, normalised (language lower case, country upper case). `ordered` always holds `['yes']`. */
  readonly values: readonly string[];
}

export interface ShowRule {
  readonly terms: readonly ShowTerm[];
}

export const MAX_SHOW_TERMS_PER_RULE = 6;
export const MAX_SHOW_VALUES_PER_TERM = 10;
/** Distinct rules in one template: keeps the per-round work bounded. */
export const MAX_SHOW_RULES_PER_TEMPLATE = 20;
/** Distinct terms across the rules of one template (one SQL expression each, in one query per round). */
export const MAX_SHOW_TERMS_PER_TEMPLATE = 8;

const VALUE_PATTERNS: Record<Exclude<ShowTermKey, 'ordered'>, RegExp> = {
  language: /^[a-z]{2}$/,
  country: /^[A-Z]{2}$/,
  segment: /^[A-Za-z0-9_-]{1,64}$/,
  label: /^[a-z0-9æøå_-]{1,40}$/,
};

const KEYS: readonly ShowTermKey[] = ['language', 'country', 'segment', 'label', 'ordered'];

export type ParseShowResult =
  { readonly ok: true; readonly rule: ShowRule } | { readonly ok: false; readonly error: string };

function clip(text: string): string {
  return text.length > 40 ? `${text.slice(0, 40)}...` : text;
}

/** Parses the text after `{{#np_show `. Strict: anything unknown is an error, never ignored. */
export function parseShowExpression(expression: string): ParseShowResult {
  const parts = expression.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { ok: false, error: 'The rule is empty.' };
  if (parts.length > MAX_SHOW_TERMS_PER_RULE) {
    return { ok: false, error: `A rule can have at most ${MAX_SHOW_TERMS_PER_RULE} conditions.` };
  }
  const terms: ShowTerm[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const m = /^([a-z]+)(!?=)(.+)$/.exec(part);
    if (!m) return { ok: false, error: `"${clip(part)}" is not a condition (use key=value).` };
    const key = m[1] as ShowTermKey;
    if (!KEYS.includes(key)) return { ok: false, error: `Unknown condition "${clip(m[1]!)}".` };
    if (seen.has(key)) return { ok: false, error: `"${key}" is used twice; combine the values.` };
    seen.add(key);
    const negate = m[2] === '!=';
    const rawValues = m[3]!.split('|');
    if (rawValues.length > MAX_SHOW_VALUES_PER_TERM) {
      return { ok: false, error: `"${key}" can have at most ${MAX_SHOW_VALUES_PER_TERM} values.` };
    }
    if (key === 'ordered') {
      if (rawValues.length !== 1 || (rawValues[0] !== 'yes' && rawValues[0] !== 'no')) {
        return { ok: false, error: 'ordered must be yes or no.' };
      }
      if (negate) return { ok: false, error: 'Use ordered=no instead of ordered!=yes.' };
      terms.push({ key, negate: rawValues[0] === 'no', values: ['yes'] });
      continue;
    }
    const values: string[] = [];
    for (const raw of rawValues) {
      const value =
        key === 'language' ? raw.toLowerCase() : key === 'country' ? raw.toUpperCase() : raw;
      if (!VALUE_PATTERNS[key].test(value)) {
        return { ok: false, error: `"${clip(raw)}" is not a valid ${key}.` };
      }
      values.push(value);
    }
    terms.push({ key, negate, values: [...new Set(values)].sort() });
  }
  terms.sort((a, b) => KEYS.indexOf(a.key) - KEYS.indexOf(b.key));
  return { ok: true, rule: { terms } };
}

/** Canonical text of one term, e.g. `language=de|fr`, `ordered=no`. */
export function formatShowTerm(term: ShowTerm): string {
  if (term.key === 'ordered') return term.negate ? 'ordered=no' : 'ordered=yes';
  return `${term.key}${term.negate ? '!=' : '='}${term.values.join('|')}`;
}

/** Canonical text of a rule: terms in a fixed order, values sorted. Two spellings of one rule give one string. */
export function formatShowRule(rule: ShowRule): string {
  return rule.terms.map(formatShowTerm).join(' ');
}

/** The term without its negation: the unit the database evaluates once per contact. */
export function termId(term: ShowTerm): string {
  return term.key === 'ordered' ? 'ordered=yes' : `${term.key}=${term.values.join('|')}`;
}

/** FNV-1a 32 bit, hex. Stable across runtimes; collisions inside one template are checked by the caller. */
function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/** The flat `TemplateData` key a rule's `{{#if}}` reads. */
export function showKey(rule: ShowRule): string {
  return `np_s_${fnv1a(formatShowRule(rule))}`;
}

/** One per-contact fact: did the contact match this term (before negation)? */
export type TermResults = ReadonlyMap<string, boolean>;

/** True when the contact matches every term. A term that was not evaluated counts as no match. */
export function evaluateShowRule(rule: ShowRule, results: TermResults): boolean {
  return rule.terms.every((t) => {
    const matched = results.get(termId(t)) === true;
    return t.negate ? !matched : matched;
  });
}

export interface ShowDirectiveError {
  readonly code:
    'bad_rule' | 'unclosed' | 'unopened' | 'too_many_rules' | 'too_many_terms' | 'hash_collision';
  readonly message: string;
}

export interface LoweredShowDirectives {
  readonly html: string;
  /** Distinct rules by key. */
  readonly rules: ReadonlyMap<string, ShowRule>;
  /** Distinct terms across all rules, by {@link termId}. */
  readonly terms: ReadonlyMap<string, ShowTerm>;
  readonly errors: readonly ShowDirectiveError[];
}

const DIRECTIVE = /\{\{#np_show(?:\s+([^{}]*?))?\s*\}\}|\{\{\/np_show\}\}/g;

/** True when `source` contains a show/hide directive at all (a cheap check before any parsing). */
export function hasShowDirectives(source: string): boolean {
  return source.includes('{{#np_show') || source.includes('{{/np_show');
}

function limitErrors(ruleCount: number, termCount: number): ShowDirectiveError[] {
  const errors: ShowDirectiveError[] = [];
  if (ruleCount > MAX_SHOW_RULES_PER_TEMPLATE) {
    errors.push({
      code: 'too_many_rules',
      message: `A template can have at most ${MAX_SHOW_RULES_PER_TEMPLATE} different show/hide rules.`,
    });
  }
  if (termCount > MAX_SHOW_TERMS_PER_TEMPLATE) {
    errors.push({
      code: 'too_many_terms',
      message: `A template can use at most ${MAX_SHOW_TERMS_PER_TEMPLATE} different conditions in its rules.`,
    });
  }
  return errors;
}

/**
 * Replaces every directive with the Handlebars `{{#if key}}`/`{{/if}}` form.
 * Text without a directive is returned as the very same string. On any error
 * the original is returned with the errors listed, so a caller can refuse.
 */
export function lowerShowDirectives(source: string): LoweredShowDirectives {
  const rules = new Map<string, ShowRule>();
  const terms = new Map<string, ShowTerm>();
  const errors: ShowDirectiveError[] = [];
  if (!hasShowDirectives(source)) return { html: source, rules, terms, errors };

  const canon = new Map<string, string>();
  let depth = 0;
  const html = source.replace(DIRECTIVE, (whole, expr: string | undefined) => {
    if (whole.startsWith('{{/')) {
      if (depth === 0) {
        errors.push({ code: 'unopened', message: '{{/np_show}} has no matching {{#np_show}}.' });
        return whole;
      }
      depth -= 1;
      return '{{/if}}';
    }
    const parsed = parseShowExpression(expr ?? '');
    if (!parsed.ok) {
      errors.push({ code: 'bad_rule', message: parsed.error });
      depth += 1; // keep the pairing, so the closing tag is not reported as well
      return whole;
    }
    depth += 1;
    const key = showKey(parsed.rule);
    const text = formatShowRule(parsed.rule);
    const known = canon.get(key);
    if (known !== undefined && known !== text) {
      errors.push({ code: 'hash_collision', message: 'Two different rules collide; change one.' });
    }
    canon.set(key, text);
    rules.set(key, parsed.rule);
    for (const t of parsed.rule.terms) terms.set(termId(t), { ...t, negate: false });
    return `{{#if ${key}}}`;
  });
  if (depth > 0) {
    errors.push({ code: 'unclosed', message: '{{#np_show}} is not closed with {{/np_show}}.' });
  }
  errors.push(...limitErrors(rules.size, terms.size));
  if (errors.length > 0) return { html: source, rules, terms, errors };
  return { html, rules, terms, errors };
}

/**
 * Removes every show/hide block with its content. The public "view in
 * browser" page has no per-contact facts, so it shows the mail without the
 * gated blocks rather than showing them to everyone or showing raw tags.
 * Text without a directive is returned as the very same string; an unbalanced
 * directive is cut from the page too (nothing raw is ever shown).
 */
export function hideShowBlocks(source: string): string {
  if (!hasShowDirectives(source)) return source;
  let out = '';
  let last = 0;
  let depth = 0;
  for (const m of source.matchAll(DIRECTIVE)) {
    const closing = m[0].startsWith('{{/');
    if (depth === 0) out += source.slice(last, m.index);
    if (closing) depth = Math.max(0, depth - 1);
    else depth += 1;
    last = m.index + m[0].length;
  }
  if (depth === 0) out += source.slice(last);
  return out;
}

/** Removes the directive tags and keeps every block's content: the editor preview that shows all blocks at once. Same string back without directives. */
export function unwrapShowBlocks(source: string): string {
  return hasShowDirectives(source) ? source.replace(DIRECTIVE, '') : source;
}

/** Validation for save time: the errors over every part (html, text, subject), limits counted across all parts. */
export function checkShowDirectives(...parts: readonly string[]): ShowDirectiveError[] {
  const allRules = new Set<string>();
  const allTerms = new Set<string>();
  const errors: ShowDirectiveError[] = [];
  for (const part of parts) {
    const r = lowerShowDirectives(part);
    errors.push(
      ...r.errors.filter((e) => e.code !== 'too_many_rules' && e.code !== 'too_many_terms'),
    );
    for (const k of r.rules.keys()) allRules.add(k);
    for (const k of r.terms.keys()) allTerms.add(k);
  }
  errors.push(...limitErrors(allRules.size, allTerms.size));
  return errors;
}

/** The segment ids a set of terms refers to (for loading them once). */
export function segmentIdsOf(terms: Iterable<ShowTerm>): string[] {
  const ids = new Set<string>();
  for (const t of terms) if (t.key === 'segment') for (const v of t.values) ids.add(v);
  return [...ids];
}

export type DeviceRule = 'all' | 'desktop' | 'mobile';

/** Head CSS for device blocks; added once to a template that uses `np-d`/`np-m`. */
export const DEVICE_CSS =
  '.np-m{display:none;max-height:0;overflow:hidden;mso-hide:all}' +
  '@media only screen and (max-width:600px){.np-d{display:none !important;max-height:0 !important;overflow:hidden !important}' +
  '.np-m{display:block !important;max-height:none !important;overflow:visible !important}}';

const MOBILE_HIDDEN_STYLE = 'display:none;max-height:0;overflow:hidden;mso-hide:all';

/**
 * Wraps a block so it shows on one kind of screen only. `desktop` hides on
 * narrow screens, `mobile` is hidden until the narrow-screen media query turns
 * it on. Clients that ignore media queries show the desktop blocks only,
 * which is the safe side: a mobile-only block is a variant of content that is
 * also there for desktop. The mobile block is also hidden inline, so a client
 * that drops the `<style>` shows the desktop variant only.
 */
export function wrapDevice(innerHtml: string, device: DeviceRule): string {
  if (device === 'all') return innerHtml;
  if (device === 'desktop') return `<div class="np-d">${innerHtml}</div>`;
  // Hidden inline too: clients that strip <style> (Gmail app with other
  // accounts, some webmails) must not show both variants.
  return `<div class="np-m" style="${MOBILE_HIDDEN_STYLE}">${innerHtml}</div>`;
}

/** Wraps a block in a show/hide directive; an empty expression means no wrapping. */
export function wrapShow(innerHtml: string, expression: string): string {
  const trimmed = expression.trim();
  return trimmed === '' ? innerHtml : `{{#np_show ${trimmed}}}${innerHtml}{{/np_show}}`;
}

/** Adds the device CSS before `</head>` (or as a leading `<style>` without a head). Idempotent; untouched when no device class is used. */
export function ensureDeviceCss(html: string): string {
  if (!/class="np-[dm]"/.test(html) || html.includes('.np-m{display:none')) return html;
  const style = `<style>${DEVICE_CSS}</style>`;
  return /<\/head>/i.test(html) ? html.replace(/<\/head>/i, `${style}</head>`) : `${style}${html}`;
}
